import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { TrendingUp, Activity, BarChart2, Gauge, CheckCircle, ArrowRight, Download } from 'lucide-react';
import AnimatedSection from '../../components/AnimatedSection';

const IndicatorsPage = () => {
  return (
    <div className="min-h-screen pt-24">
      {/* Hero */}
      <section className="py-24 bg-gradient-to-br from-[#0B0F1A] via-[#1a1147] to-[#0B0F1A] relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-[#7C3AED]/10 via-transparent to-[#00D4FF]/10" />
        
        <div className="relative z-10 max-w-[1280px] mx-auto px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <p className="text-[#00D4FF] mb-4 text-lg">Professional Trading Indicator</p>
            <h1 className="text-6xl font-bold mb-6">Indicators</h1>
            <p className="text-2xl text-[#9CA3AF] max-w-3xl mx-auto">
              Master the markets with our comprehensive indicator designed for professional order flow analysis
            </p>
          </motion.div>
        </div>
      </section>

      {/* Download Section - In the middle */}
      <section className="py-24 bg-[#0B0F1A]">
        <div className="max-w-[1280px] mx-auto px-8">
          <AnimatedSection>
            <div className="bg-gradient-to-br from-[#7C3AED]/10 to-[#00D4FF]/10 border border-white/10 rounded-3xl p-12 text-center relative overflow-hidden flex flex-col lg:flex-row items-center gap-12">
              <div className="relative z-10 flex-1 text-left lg:pl-8">
                <h2 className="text-4xl font-bold mb-6">Download Our Premium MT5 Indicator</h2>
                <p className="text-xl text-[#9CA3AF] mb-10 max-w-2xl">
                  Get instant access to our advanced MT5 trading indicator. Built specifically for MetaTrader 5 with high-performance execution.
                </p>
                
                <div className="flex flex-col sm:flex-row items-start gap-6">
                  <Link to="/signup">
                    <motion.button
                      className="bg-[#7C3AED] text-white px-10 py-4 rounded-xl font-bold text-lg flex items-center gap-3 hover:shadow-2xl hover:shadow-[#7C3AED]/50 transition-all"
                      whileHover={{ scale: 1.05, translateY: -4 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <Download size={24} /> Download for MT5
                    </motion.button>
                  </Link>
                </div>
                
                <p className="mt-8 text-sm text-[#9CA3AF]">
                  Compatible with MetaTrader 5 (MT5) Desktop
                </p>
              </div>

              {/* 3D Animation Section */}
              <div className="flex-1 flex items-center justify-center perspective-1000">
                <motion.div
                  className="w-64 h-64 bg-gradient-to-br from-[#7C3AED] to-[#00D4FF] rounded-2xl shadow-2xl relative flex items-center justify-center"
                  animate={{
                    rotateY: [0, 360],
                    rotateX: [10, -10, 10],
                    y: [0, -20, 0]
                  }}
                  transition={{
                    duration: 8,
                    repeat: Infinity,
                    ease: "linear"
                  }}
                  style={{
                    transformStyle: "preserve-3d"
                  }}
                >
                  <div className="absolute inset-0 bg-white/10 backdrop-blur-sm rounded-2xl" />
                  <div className="relative z-20 flex flex-col items-center">
                    <Activity size={80} className="text-white mb-4" />
                    <span className="text-white font-bold text-2xl">MT5</span>
                    <span className="text-white/80 text-sm">INDICATOR</span>
                  </div>
                  
                  {/* Floating elements for 3D depth */}
                  <motion.div 
                    className="absolute -top-4 -right-4 w-12 h-12 bg-[#00D4FF] rounded-lg flex items-center justify-center shadow-lg"
                    animate={{ translateZ: [20, 50, 20] }}
                    transition={{ duration: 4, repeat: Infinity }}
                  >
                    <CheckCircle size={24} className="text-white" />
                  </motion.div>
                </motion.div>
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Features */}
      <section className="py-24 bg-[#0F172A]">
        <div className="max-w-[1280px] mx-auto px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <AnimatedSection>
              <div className="rounded-2xl overflow-hidden border border-white/10 bg-gradient-to-br from-[#1a1147] to-[#0B0F1A] p-1">
                <div className="aspect-video bg-gradient-to-br from-[#7C3AED]/20 via-[#ec4899]/20 to-[#00D4FF]/20 rounded-xl flex items-center justify-center">
                  <Activity size={120} className="text-white/40" />
                </div>
              </div>
            </AnimatedSection>

            <AnimatedSection delay={0.2}>
              <h2 className="text-4xl font-bold mb-6">Powerful Analysis Features</h2>
              <p className="text-[#9CA3AF] mb-6">
                Our indicator is designed to work seamlessly with order flow data, providing insights that traditional indicators simply cannot offer.
              </p>
              <ul className="space-y-4">
                {[
                  'Real-time calculations',
                  'Multi-timeframe analysis',
                  'Custom alert configurations',
                  'Backtesting capabilities'
                ].map((feature, index) => (
                  <motion.li
                    key={`adv-feature-${index}`}
                    className="flex items-center gap-3"
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <CheckCircle className="text-[#00D4FF]" size={20} />
                    <span>{feature}</span>
                  </motion.li>
                ))}
              </ul>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-gradient-to-br from-[#7C3AED]/20 via-[#ec4899]/10 to-[#00D4FF]/20">
        <div className="max-w-[1280px] mx-auto px-8 text-center">
          <AnimatedSection>
            <h2 className="text-4xl font-bold mb-6">Access our professional indicator</h2>
            <p className="text-xl text-[#9CA3AF] mb-8 max-w-2xl mx-auto">
              Start analyzing markets like a professional with our powerful indicator suite
            </p>
            <Link to="/signup">
              <motion.button
                className="bg-[#7C3AED] text-white px-10 py-4 rounded-lg font-bold text-lg hover:shadow-2xl hover:shadow-[#7C3AED]/50 transition-all"
                whileHover={{ scale: 1.05, translateY: -4 }}
                whileTap={{ scale: 0.98 }}
              >
                Start Free Trial <ArrowRight className="inline ml-2" size={20} />
              </motion.button>
            </Link>
          </AnimatedSection>
        </div>
      </section>
    </div>
  );
};

export default IndicatorsPage;